// Libs
import clsx from "clsx";

// Utils
import { useTranslation } from "src/design-system/core";
import { wrapLabel } from "src/design-system/core/utils";
import { type Channel, ChannelCategory, ChannelSourceEnum } from "src/types/channel";
import type { ThirdServiceCategory } from "src/types/posCredential";

// Components
import { Icon, type IconProps } from "src/design-system/molecules/icons";
import { Tooltip } from "src/design-system/molecules/modals";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import { formatChannelList, type FormatterOptions } from "src/utils/formatters";
import {
    ClickNCollectAppIcon,
    ClickNCollectWebIcon,
    DeliverooIcon,
    GlovoIcon,
    JustEatIcon,
    OtherIcon,
    UberEatsIcon,
} from "../icons/brand-logos-new"; // Une fois validé, supprimer l'ancien folder et retirer le new du nouveau

export const CHANNEL_SOURCE_ENUM_ICON_MAP: Record<ChannelSourceEnum, React.ElementType> = {
    [ChannelSourceEnum.App]: ClickNCollectAppIcon,
    [ChannelSourceEnum.JustEat]: JustEatIcon,
    [ChannelSourceEnum.Deliveroo]: DeliverooIcon,
    [ChannelSourceEnum.Glovo]: GlovoIcon,
    [ChannelSourceEnum.Other]: OtherIcon,
    [ChannelSourceEnum.Pos]: () => <span>POS</span>,
    [ChannelSourceEnum.Ubereats]: UberEatsIcon,
    [ChannelSourceEnum.Web]: ClickNCollectWebIcon,
};

const POS_CATEGORY_ENUM_ICON_MAP: Record<ChannelCategory, string> = {
    [ChannelCategory.EatIn]: "channel-eat-in",
    [ChannelCategory.TakeAway]: "channel-takeout",
    [ChannelCategory.TakeAwayDelivery]: "channel-delivery",
    [ChannelCategory.Delivery]: "channel-delivery",
};

export interface ChannelIconDisplayProps {
    /**
     * Channel
     */
    channel?: Channel;
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional className
     */
    iconClassName?: string;
    /**
     * Additional icon props
     */
    iconProps?: IconProps;
    /**
     * Service
     */
    service?: ThirdServiceCategory;
    /**
     * noLabel
     */
    noLabel?: boolean;
}

export function ChannelIconDisplay({
    channel,
    className: classNameProp,
    iconClassName: iconClassNameProp,
    iconProps,
    service,
    noLabel = false,
}: ChannelIconDisplayProps): JSX.Element {
    const { translateCommon } = useTranslation();

    const className = clsx("fl-c-channel-icon-display", classNameProp);
    const trueSource = service ?? channel.source;
    const IconComponent = CHANNEL_SOURCE_ENUM_ICON_MAP[trueSource];
    let icon;
    const isPosChannel = trueSource === ChannelSourceEnum.Pos && channel;
    if (isPosChannel) {
        icon = POS_CATEGORY_ENUM_ICON_MAP[channel.category]; // className="--ds-color--primary-500" />
    }
    const iconClassName = clsx("fl-c-channel-icon-display__icon", iconClassNameProp, {
        ["fl-c-channel-icon-display__icon--blue"]: isPosChannel,
    });

    const label = translateCommon(
        channel?.source === ChannelSourceEnum.Other ||
            channel?.category === ChannelCategory.Delivery
            ? `channel.${channel.name}`
            : `enums.mode.${channel?.category}`
    );

    return (
        <div className={className}>
            <Icon {...iconProps} className={iconClassName} icon={icon}>
                <IconComponent />
            </Icon>
            {channel &&
                !noLabel &&
                wrapLabel(label, "fl-c-channel-icon-display__mode ds-u-typography--caption")}
        </div>
    );
}

export function ChannelIconDisplayList({
    channelList,
    formatOptions,
}: {
    channelList: Channel[];
    formatOptions: FormatterOptions;
}): JSX.Element {
    const { translateRaw } = formatOptions;

    if (channelList.length === 1) {
        return <ChannelIconDisplay channel={channelList[0]} />;
    }

    return (
        <Tooltip
            tooltip={channelList
                .map(
                    (channel) =>
                        `${translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:channel.${channel.name}`)}`
                )
                .join(", ")}
        >
            <div>
                <span>{formatChannelList(channelList, formatOptions)}</span>
            </div>
        </Tooltip>
    );
}
